import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SignupTimelineChart from 'src/common/components/signup-timeline/SignupTimelineChart';
import { LanguageKeys } from 'src/common/constants/languages';
import { frostedPanelSx } from 'src/common/styles/frostedPanelStyles';
import {
  getEditionCompetitionOptions,
  getEditionDistanceOptions,
} from 'src/helpers/signupTimeline';
import { useSignupTimeline } from 'src/hooks/useSignupTimeline';
import { IEdition } from 'src/interfaces/Edition';
import { SignupTimelinePoint } from 'src/interfaces/SignupTimeline';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

interface Props {
  edition?: IEdition | null;
  /** When set, chart uses these points and does not fetch. */
  points?: SignupTimelinePoint[];
  /** Show competition/distance filters (API-backed mode only). */
  showFilters?: boolean;
  title?: string;
  embedded?: boolean;
  chartHeight?: number;
  /** Include unpublished competitions/distances (admin historical views). */
  includeUnpublished?: boolean;
}

const panelSx = {
  ...frostedPanelSx(0.5),
  width: '100%',
  p: 2,
} as const;

const SignupTimelineSection = ({
  edition,
  points: externalPoints,
  showFilters = true,
  title,
  embedded = false,
  chartHeight,
  includeUnpublished = false,
}: Props) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;

  const { competitionsById, distancesById } = useSystemOptionsStore(s => s);

  const [competitionId, setCompetitionId] = useState<string | null>(null);
  const [distanceId, setDistanceId] = useState<string | null>(null);

  const competitions = useMemo(
    () => getEditionCompetitionOptions(edition, competitionsById, { includeUnpublished }),
    [edition, competitionsById, includeUnpublished],
  );

  const distances = useMemo(
    () =>
      getEditionDistanceOptions(edition, {
        competitionId,
        distancesById,
        includeUnpublished,
      }),
    [edition, competitionId, distancesById, includeUnpublished],
  );

  useEffect(() => {
    setCompetitionId(null);
    setDistanceId(null);
  }, [edition?.id]);

  useEffect(() => {
    if (distanceId && !distances.some(d => d.id === distanceId)) {
      setDistanceId(null);
    }
  }, [distanceId, distances]);

  const fetchEnabled = externalPoints == null && Boolean(edition?.id);
  const { points: fetchedPoints, isLoading, error } = useSignupTimeline({
    editionId: edition?.id,
    competitionId: showFilters ? competitionId : null,
    distanceId: showFilters ? distanceId : null,
    enabled: fetchEnabled,
  });

  const points = externalPoints ?? fetchedPoints;
  const hasData = points.length > 0;
  const heading = title ?? t('signups.timeline.title');

  const filters =
    showFilters && externalPoints == null ? (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="signup-timeline-competition-label" shrink>
            {t('events.event')}
          </InputLabel>
          <Select
            labelId="signup-timeline-competition-label"
            label={t('events.event')}
            value={competitionId ?? ''}
            displayEmpty
            onChange={e => {
              setCompetitionId(e.target.value || null);
              setDistanceId(null);
            }}
          >
            <MenuItem value="">{t('base.all')}</MenuItem>
            {competitions.map(c => (
              <MenuItem key={c.id} value={c.id}>
                {c.i18n[currentLang]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="signup-timeline-distance-label" shrink>
            {t('events.distance')}
          </InputLabel>
          <Select
            labelId="signup-timeline-distance-label"
            label={t('events.distance')}
            value={distanceId ?? ''}
            displayEmpty
            onChange={e => setDistanceId(e.target.value || null)}
          >
            <MenuItem value="">{t('base.all')}</MenuItem>
            {distances.map(d => (
              <MenuItem key={d.id} value={d.id}>
                {d.i18n[currentLang]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    ) : null;

  const body = (
    <>
      <Typography variant="subtitle1" component="h3" sx={{ mb: 1 }}>
        {heading}
      </Typography>
      {filters}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : error ? (
        <Typography variant="body2" color="text.secondary">
          {t('signups.timeline.loadError')}
        </Typography>
      ) : !hasData ? (
        <Typography variant="body2" color="text.secondary">
          {t('signups.timeline.empty')}
        </Typography>
      ) : (
        <SignupTimelineChart points={points} height={chartHeight} />
      )}
    </>
  );

  if (embedded) {
    return <Box sx={{ width: '100%' }}>{body}</Box>;
  }

  return <Paper sx={panelSx}>{body}</Paper>;
};

export default SignupTimelineSection;
